import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Search, User, LogOut, Settings, UserCircle, MessageSquare, Download, ArrowRight, Eye, Layers } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { userApi, UserInfoData } from "@/api/modules/user";
import { toast } from "sonner";

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // 用户信息状态
  const [userInfo, setUserInfo] = useState<UserInfoData | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // 产品下载菜单数据
  const downloadProducts = [
    {
      id: "seeod",
      name: "SeeOD",
      title: "几何光学仿真",
      icon: <Eye className="w-4 h-4" />,
      color: "blue"
    },
    {
      id: "seetf",
      name: "SeeTF",
      title: "薄膜优化与误差分析",
      icon: <Layers className="w-4 h-4" />,
      color: "purple"
    }
  ];

  // // 检查登录状态并获取用户信息
  // useEffect(() => {
  //   const checkAuthAndFetchUser = async () => {
  //     const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  //     const userInfo = localStorage.getItem('userInfo');
  //
  //     if (token && userInfo) {
  //       setIsAuthenticated(true);
  //       setUserInfo(JSON.parse(userInfo || "{}"));
  //     } else {
  //       // 获取用户信息
  //       try {
  //         const response = await userApi.getUserDetail();
  //         if (response?.data) {
  //           setIsAuthenticated(true);
  //           setUserInfo(response.data);
  //           // 保存到 localStorage 供其他页面使用
  //           localStorage.setItem('userInfo', JSON.stringify(response.data));
  //         }
  //       } catch (error) {
  //         console.error('获取用户信息失败:', error);
  //         // 如果获取失败，尝试从 localStorage 读取
  //         setIsAuthenticated(false);
  //         setUserInfo(null);
  //       }
  //     }
  //   };
  //
  //   checkAuthAndFetchUser();
  // }, []);

  // 退出登录
  const handleLogout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    setIsAuthenticated(false);
    setUserInfo(null);
    navigate('/');
  };

  const navItems = [
    { path: "/", label: "首页" },
    { path: "/about", label: "公司简介" },
    { path: "/products", label: "产品" },
    // { path: "/manual", label: "参考手册" },
    { path: "/cases", label: "案例" },
    // { path: "/community", label: "社区" },
    { path: "/contact", label: "联系我们" },
  ];

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <img src="/images/logo.png" alt="Logo" className="w-8 h-8" />
              <span className="text-xl font-bold text-gray-900">杭州光智云</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-3 py-2 text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-700 hover:text-blue-600"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Search and User Info */}
          <div className="hidden md:flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="搜索..."
                className="pl-10 w-48"
              />
            </div>
            
            {/* 产品下载下拉菜单 */}
            <DropdownMenu open={isDownloadMenuOpen} onOpenChange={setIsDownloadMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  产品下载
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-72" align="end" forceMount>
                <DropdownMenuLabel className="px-4 py-3 font-semibold text-gray-900">
                  选择产品下载
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {downloadProducts.map((product) => {
                  const colorMap = {
                    blue: "bg-blue-50 border-blue-200 hover:bg-blue-100",
                    purple: "bg-purple-50 border-purple-200 hover:bg-purple-100"
                  };
                  const colorClass = colorMap[product.color as keyof typeof colorMap];
                  
                  const itemContent = (
                    <div className={`w-full px-4 py-3 border-l-4 ${product.color === 'blue' ? 'border-blue-600' : 'border-purple-600'} ${colorClass} flex items-start gap-3 cursor-pointer transition-colors`}>
                      <div className={`p-2 rounded ${product.color === 'blue' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'}`}>
                        {product.icon}
                      </div>
                      <div className="flex-1 text-left">
                        {/* <div className="font-semibold text-gray-900">{product.name}</div> */}
                        <div className="text-sm text-gray-600">{product.title}</div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
                    </div>
                  );

                  if (product.id === 'seeod') {
                    return (
                      <DropdownMenuItem key={product.id} asChild>
                        <Link to="/version-comparison" className="no-underline">
                          {itemContent}
                        </Link>
                      </DropdownMenuItem>
                    );
                  } else {
                    return (
                      <DropdownMenuItem key={product.id} asChild>
                        <button 
                          onClick={() => {
                            toast.custom(
                              (t) => (
                                <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 max-w-md mx-auto flex items-start gap-3 animate-in fade-in slide-in-from-top-2">
                                  <div className="flex-shrink-0 pt-1">
                                    <div className="flex items-center justify-center h-8 w-8 rounded-full bg-purple-100">
                                      <span className="text-purple-600 font-semibold text-lg">!</span>
                                    </div>
                                  </div>
                                  <div className="flex-1">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-1">开发中</h3>
                                    <p className="text-sm text-gray-600">感谢你的关注，该功能正在全力开发中。</p>
                                  </div>
                                  <button
                                    onClick={() => toast.dismiss(t)}
                                    className="flex-shrink-0 text-gray-400 hover:text-gray-600"
                                  >
                                    <X className="w-5 h-5" />
                                  </button>
                                </div>
                              ),
                              {
                                position: "top-center",
                                duration: 3000,
                              }
                            );
                            setIsDownloadMenuOpen(false);
                          }}
                          className="w-full"
                        >
                          {itemContent}
                        </button>
                      </DropdownMenuItem>
                    );
                  }
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* 用户信息下拉框 */}
            {isAuthenticated && userInfo && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={userInfo?.avatar} alt={userInfo.nickname || userInfo.username} />
                      <AvatarFallback className="bg-blue-100 text-blue-600">
                        {(userInfo.nickname || userInfo.username || 'U').charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{userInfo.nickname || userInfo.username}</p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {userInfo.email || userInfo.phone}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <UserCircle className="mr-2 h-4 w-4" />
                    <span>个人信息</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile/topics')}>
                    <MessageSquare className="mr-2 h-4 w-4" />
                    <span>我的讨论</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile/settings')}>
                    <Settings className="mr-2 h-4 w-4" />
                    <span>设置</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>退出登录</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 border-t border-gray-200">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block px-3 py-2 text-base font-medium transition-colors ${
                    isActive(item.path)
                      ? "text-blue-600 bg-blue-50"
                      : "text-gray-700 hover:text-blue-600 hover:bg-gray-50"
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
              
              {/* Mobile Product Download Dropdown */}
              <div className="px-3 py-2">
                <div className="mb-2 text-sm font-semibold text-gray-600">产品下载</div>
                <div className="space-y-2 bg-gray-50 rounded-lg p-2">
                  {downloadProducts.map((product) => {
                    const colorMap = {
                      blue: "bg-blue-50 border-l-4 border-blue-600",
                      purple: "bg-purple-50 border-l-4 border-purple-600"
                    };
                    const colorClass = colorMap[product.color as keyof typeof colorMap];
                    const iconColorMap = {
                      blue: "bg-blue-100 text-blue-600",
                      purple: "bg-purple-100 text-purple-600"
                    };
                    const iconColorClass = iconColorMap[product.color as keyof typeof iconColorMap];
                    
                    const content = (
                      <>
                        <div className={`p-1.5 rounded flex-shrink-0 ${iconColorClass}`}>
                          {product.icon}
                        </div>
                        <div className="flex-1">
                          {/* <div className="font-medium text-gray-900 text-sm">{product.name}</div> */}
                          <div className="text-xs text-gray-600">{product.title}</div>
                        </div>
                      </>
                    );

                    if (product.id === 'seeod') {
                      return (
                        <Link
                          key={product.id}
                          to="/version-comparison"
                          className={`w-full px-3 py-2 ${colorClass} text-left rounded flex items-start gap-2 transition-colors hover:opacity-80 no-underline`}
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {content}
                        </Link>
                      );
                    } else {
                      return (
                        <button
                          key={product.id}
                          className={`w-full px-3 py-2 ${colorClass} text-left rounded flex items-start gap-2 transition-colors hover:opacity-80`}
                          onClick={() => {
                            toast.custom(
                              (t) => (
                                <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 max-w-md mx-auto flex items-start gap-3 animate-in fade-in slide-in-from-top-2">
                                  <div className="flex-shrink-0 pt-1">
                                    <div className="flex items-center justify-center h-8 w-8 rounded-full bg-purple-100">
                                      <span className="text-purple-600 font-semibold text-lg">!</span>
                                    </div>
                                  </div>
                                  <div className="flex-1">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-1">开发中</h3>
                                    <p className="text-sm text-gray-600">感谢你的关注，该功能正在全力开发中。</p>
                                  </div>
                                  <button
                                    onClick={() => toast.dismiss(t)}
                                    className="flex-shrink-0 text-gray-400 hover:text-gray-600"
                                  >
                                    <X className="w-5 h-5" />
                                  </button>
                                </div>
                              ),
                              {
                                position: "top-center",
                                duration: 3000,
                              }
                            );
                            setIsMenuOpen(false);
                          }}
                        >
                          {content}
                        </button>
                      );
                    }
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;